/**
 * 以受限并发执行异步映射，返回顺序与输入顺序保持一致。
 *
 * 设计说明：模型加载可以并行下载和解析多个独立资源，但不能让大装配一次性发起无限请求。
 * 这里把“并发调度”收口为纯工具函数，调用方只负责声明任务列表、并发上限和单项加载逻辑。
 *
 * @param items 输入列表。
 * @param concurrency 最大并发数。
 * @param mapper 异步映射函数。
 * @returns 按输入顺序排列的映射结果。
 */
export async function mapWithConcurrencyLimit<TInput, TOutput>(
  items: readonly TInput[],
  concurrency: number,
  mapper: (item: TInput, index: number) => Promise<TOutput>,
): Promise<TOutput[]> {
  if (items.length === 0) {
    return []
  }

  const results = new Array<TOutput>(items.length)
  let nextIndex = 0
  const workerCount = Math.max(1, Math.min(concurrency, items.length))

  async function consume(): Promise<void> {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex
      nextIndex += 1
      results[currentIndex] = await mapper(items[currentIndex], currentIndex)
    }
  }

  await Promise.all(
    Array.from({ length: workerCount }, () => consume()),
  )
  return results
}
